import { DateTime, type Interval } from 'luxon';
import { type Result, ResultAsync } from 'neverthrow';

export const notEmpty = <TValue>(value: TValue | null | undefined): value is TValue => {
	return value !== null && value !== undefined;
};

export const insertIf = <T>(condition: boolean | string | undefined, ...elements: T[]) => {
	return condition ? elements : [];
};

export const sortAlphabeticallyBy = <T extends object>(key: keyof T, ascending = false) => {
	return (a: T, b: T): number => {
		const stringA = String(a[key]).toLocaleLowerCase();
		const stringB = String(b[key]).toLocaleLowerCase();

		if (stringA < stringB) return ascending ? 1 : -1;
		if (stringA > stringB) return ascending ? -1 : 1;

		return 0;
	};
};

export const isTrueValue = (input: unknown) => {
	if (typeof input === 'boolean') {
		return input;
	}

	if (typeof input === 'string') {
		return input.toLowerCase() === 'true';
	}

	return false;
};

export const ensureArray = <T>(input: T[] | undefined | null): T[] => {
	return input ?? [];
};

export const truncateString = (string: string, length: number) => {
	if (string.length > length) {
		return string.slice(0, length) + '...';
	}

	return string;
};

export const groupBy = <T>(array: T[], predicate: (value: T, index: number, array: T[]) => string) =>
	array.reduce((acc, value, index, _array) => {
		(acc[predicate(value, index, _array)] ||= []).push(value);
		return acc;
	}, {} as { [key: string]: T[] });

export const hexToRgb = (hex: string) => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
			r: parseInt(result[1] || '0', 16),
			g: parseInt(result[2] || '0', 16),
			b: parseInt(result[3] || '0', 16),
		}
		: null;
};

export const wait = async (durationInMs: number) => {
	return new Promise((resolve) => setTimeout(resolve, durationInMs));
};

export const compareIsoDates = (a: string, b: string) => {
	return DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis();
};

/**
 * This function converts a `Promise<Result<T, E>>` into a `ResultAsync<T, E>`.
 * It is crucial to ensure that the `Promise` does not throw unexpected errors.
 * Ideally, the `Promise` should include a `try ... catch` block to guarantee
 * that it is a "safe promise".
 * Example usage:
 * const promiseFunction = wrapResultAsyncFunction(async (): Promise<Result<T, E>> => {});
 */
export const wrapResultAsyncFunction = <A extends unknown[], T, E>(
	func: (...args: A) => Promise<Result<T, E>>,
): (...args: A) => ResultAsync<T, E> => (...args): ResultAsync<T, E> => new ResultAsync(func(...args));

export const dateTimeTransformWithTime = {
	get: (value: string) => {
		const dateTime = DateTime.fromISO(value);
		return dateTime.isValid ? dateTime : undefined;
	},
	set: (value: DateTime | undefined) => value?.toISO() || '',
};

export const deepClone = <T>(input: T): T => JSON.parse(JSON.stringify(input));

export const isIsoDate = (input: string) => DateTime.fromISO(input).isValid;

export const getFormattedFollowerCount = (follower: number) => {
	const formatter = new Intl.NumberFormat('en', {
		notation: 'compact',
		compactDisplay: 'short',
		maximumFractionDigits: 1,
	});
	return formatter.format(follower);
};

/**
 * Converts a JavaScript Date object to an ISO 8601 string in the format expected by the API
 * (e.g., "2025-08-30T22:00:00+00:00" instead of "2025-08-30T22:00:00Z").
 *
 * Luxon typically outputs "Z" for UTC dates when using toISO() without specific options.
 * This function ensures the explicit offset "+00:00" is used for consistency and to prevent
 * any potential misinterpretation of dates across systems. A fallback replacement is applied
 * in case Luxon unexpectedly outputs "Z".
 *
 * @param {Date} date - The JavaScript Date object to convert.
 * @returns {string} The formatted ISO string in API-compatible format.
 * @throws {Error} If the date is invalid or conversion fails.
 */
export const getIsoDateInApiFormat = (date: Date): string => {
	const result = DateTime.fromJSDate(date).toUTC().toISO({ includeOffset: true, suppressMilliseconds: true })?.replace('Z', '+00:00');

	if (!result) {
		throw new Error('Invalid date');
	}

	return result;
};

export const isStartOfMonth = (d: unknown): boolean => {
	if (!DateTime.isDateTime(d)) {
		return false;
	}

	return d.hasSame(DateTime.now().startOf('month'), 'day');
};

export const isStartOfYear = (d: unknown): boolean => {
	if (!DateTime.isDateTime(d)) {
		return false;
	}

	return d.hasSame(DateTime.now().startOf('year'), 'day');
};

export const isToday = (d: unknown): boolean => {
	if (!DateTime.isDateTime(d)) {
		return false;
	}

	return d.hasSame(DateTime.now(), 'day');
};

export const getCompactReach = (reach: number) => Intl.NumberFormat('en', { notation: 'compact' }).format(reach);

export const escapeHtmlAttribute = (value: string): string =>
	value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const createDataAttribute = (key: string, value: unknown): [string, string][] =>
	value == null || value === '' ? [] : [[`data-${key}`, String(value)]];

export const isValidDateTime = (dateTime: DateTime): dateTime is DateTime<true> => dateTime.isValid;

export const isValidInterval = (interval: Interval): interval is Interval<true> => interval.isValid;

export const sumPoints = (points: number[] | undefined) => points?.reduce((total, value) => total + value, 0);

export const sumPointsByIndex = (series: (number[] | undefined)[]): number[] => {
	const length = Math.max(0, ...series.map((points) => points?.length ?? 0));

	return Array.from({ length }, (_, index) => series.reduce((total, points) => total + (points?.[index] ?? 0), 0));
};
